// Widget System Types
export enum WidgetType {
  SCENE_ANALYSIS = 'scene_analysis',
  DRIFT_ANALYSIS = 'drift_analysis',
  DELAY_ANALYSIS = 'delay_analysis',
  PRODUCTION_SUMMARY = 'production_summary',
  PRODUCTION_DAILY_FOCUS = 'production_daily_focus',
  BUDGET_TRACKING = 'budget_tracking',
  PRODUCTION_TIMELINE = 'production_timeline',
  TIMING_TOWER = 'timing_tower',
  PRODUCTION_ASSUMPTIONS = 'production_assumptions',
  CAST_SCHEDULE = 'cast_schedule',
  WEATHER_MONITOR = 'weather_monitor',
  CUSTOM_CHART = 'custom_chart',
  TEXT_DISPLAY = 'text_display',
  DOCUMENT_STATUS = 'document_status',
  DOCUMENT_LIBRARY = 'document_library',
  PRODUCTION_VELOCITY = 'production_velocity',
  FINANCIAL_TRACKING = 'financial_tracking',
}

export interface WidgetConfig {
  id: string;
  type: WidgetType;
  title: string;
  position: WidgetPosition;
  size: WidgetSize;
  settings: Record<string, any>;
  dataSource?: string;
  refreshInterval?: number;
  isVisible: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface WidgetPosition {
  x: number;
  y: number;
  z: number;
}

export interface WidgetSize {
  width: number;
  height: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export interface DashboardConfig {
  id: string;
  userId: string;
  name: string;
  widgets: WidgetConfig[];
  layout: DashboardLayout;
  theme: DashboardTheme;
  createdAt: Date;
  updatedAt: Date;
}

export interface DashboardLayout {
  columns: number;
  rows: number;
  gap: number;
  padding: number;
}

export interface DashboardTheme {
  mode: 'dark' | 'light';
  primaryColor: string;
  backgroundColor: string;
  cardBackgroundColor: string;
  textColor: string;
  accentColor: string;
}

// Scene Analysis Widget Specific Types
export interface SceneAnalysisData {
  day: number;
  date: string;
  plannedScenes: PlannedScene[];
  actualScenes: ActualScene[];
  analysis: SceneAnalysis;
  scheduleVersion: string;
}

export interface PlannedScene {
  sceneNumber: string;
  description: string;
  pages: number | string;
  characterCount?: number;
  dayNightDesignation: string;
  priorityLevel: number;
  location: string;
  estimatedDuration?: string;
}

export interface ActualScene {
  sceneNumber: string;
  description: string;
  pagesShot: number;
  timeStarted?: string;
  timeCompleted?: string;
  notes?: string;
  status: 'completed' | 'partial' | 'not_shot';
}

export interface SceneAnalysis {
  totalPlannedPages: number | string;
  totalShotPages: number | string;
  dailyVariance: number | string;
  dailyVarianceSign?: string;
  dailyVarianceNumeric?: number;
  cumulativeDrift: number;
  plannedPagesDisplay?: string;
  scenesNotShot: string[];
  scenesShotNotPlanned: string[];
  reasons: SceneDiscrepancyReason[];
  status: 'ahead' | 'on_track' | 'behind' | 'critical' | 'pending';
  summary?: string;
}

export interface SceneDiscrepancyReason {
  sceneNumber: string;
  reason: string;
  category: 'weather' | 'location' | 'cast' | 'crew' | 'equipment' | 'schedule' | 'other';
  impact: 'low' | 'medium' | 'high';
}

export interface EnhancedDriftAnalysisData {
  shoot_day_number: number;
  shoot_date: string;
  schedule_version: string;
  schedule_revision_date: string;
  original_planned_pages: string;
  revised_planned_pages: string;
  actual_shot_pages: string;
  daily_variance: string;
  daily_variance_numeric: number;
  cumulative_drift_original: string;
  cumulative_drift_original_numeric: number;
  cumulative_drift_revised: string;
  cumulative_drift_revised_numeric: number;
  pre_revision_drift: string;
  post_revision_performance: string;
  schedule_effectiveness: 'exceeded_target' | 'met_target' | 'slightly_behind' | 'significantly_behind';
  status: 'ahead' | 'on_track' | 'behind' | 'critical';
  revision_impact: string;
  daily_drift_progression: string;
  // New fields from updated get_dynamic_drift_analysis() function
  current_plan_name?: string;
  cumulative_since_latest_revision?: string;
  cumulative_since_latest_revision_numeric?: number;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}

export interface WidgetDataResponse {
  widgetId: string;
  data: any;
  lastUpdated: string;
  nextRefresh?: string;
}

// Delay Analysis Widget Specific Types
export interface DelayAnalysisData {
  delayReasons: DelayReasonData[];
  delayTiming: DelayTimingData;
  delayIncidents: DelayIncidentData[];
  totalHoursLost: number;
}

export interface DelayReasonData {
  delay_reason: string;
  total_hours_lost: number;
  occurrence_count: number;
  avg_hours_per_occurrence: number;
}

export interface DelayTimingData {
  first_2_hours: number;
  rest_of_day: number;
  first_2_hours_percentage: number;
  rest_of_day_percentage: number;
}

export interface DelayIncidentData {
  shoot_day_number: number;
  shoot_date: string;
  delay_reason: string;
  hours_lost: number;
  delay_start_time?: string;
  delay_end_time?: string;
  source_document?: string;
}

// Production Daily Focus Widget Specific Types
export interface ProductionRiskData {
  severity: 'Critical' | 'High' | 'Medium' | 'Info';
  type: 'Turnaround' | 'Complex Scene' | 'Pacing' | 'Carried Scene' | 'Delay Pattern' | 'Prop' | 'Weather' | 'Other';
  alert: string;
  details: string;
}

export interface ProductionDailyFocusData {
  shootDayNumber: number;
  shootDate: string;
  risks: ProductionRiskData[];
}

// --- Appended for TimingTowerWidget ---

/**
 * Represents the detailed data for a single shoot day.
 * Used for both the tower list and the pop-up summary panel.
 */
export type TimingTowerDocumentType =
  | 'call_sheet'
  | 'lunch'
  | 'wrap'
  | 'production_report';

export interface TimingTowerUpdateSource {
  documentType: TimingTowerDocumentType;
  label: string;
  reportedOn?: string | null;
  updatedAt?: string | null;
  referenceId?: string;
  notes?: string;
}

export interface TimingTowerCharacter {
  characterId: string | null;
  characterName: string;
  actorName: string | null; // null if actor name not available
  callTime: string | null; // null if call time not available (from Call Sheet - planned)
  actualTime: string | null; // null if actual arrival time not available (from DPR - actual)
  arrivalVarianceMinutes?: number | null;
}

export interface TimingTowerDay {
  dayNumber: number;
  status: 'completed' | 'in_progress' | 'planned' | 'critical' | 'scheduled';
  statusColor?: string; // e.g., "#48BB78", "#F56565" (driven by backend logic)
  scenes: string[] | number; // legacy alias for planned scenes, can be count or array
  scenesPlanned: string[] | number; // can be array of scene IDs or count
  scenesShot: string[] | number; // can be array of scene IDs or count
  plannedScenesCount?: number;
  shotScenesCount?: number;
  
  // String versions for display
  plannedPages: string; // e.g., "4 2/8"
  actualPages: string; // e.g., "4 2/8"
  
  // Numeric versions for calculations
  plannedPagesNumeric: number; // e.g., 4.25
  actualPagesNumeric: number; // e.g., 4.25
  
  progressPercent: number; // e.g., 100 (for the *daily* progress)
  
  // Data for the on-click summary panel (optional until call sheets/DPRs are parsed)
  crewCallTime?: string | null; // e.g., "06:00"
  actualFirstShotTime?: string | null; // e.g., "07:15"
  characters?: TimingTowerCharacter[];
  punctualitySummary?: {
    onTimeCount: number;
    lateCount: number;
    avgVarianceMinutes: number | null;
  } | null;
  firstLocation?: {
    name: string;
    status: 'INT' | 'EXT' | string;
  };
  weather?: {
    forecast: string; // e.g., "Sunny"
    temp: string; // e.g., "75°F / 24°C"
  };
  lastUpdatedBy?: TimingTowerUpdateSource | null;
}

/**
 * The main data object for the TimingTowerWidget.
 */
export interface TimingTowerData {
  currentScriptVersion: string; // e.g., "Strip Schedule"
  scriptColor: string | null; // e.g., "GOLDENROD"
  scriptColorHex: string | null; // e.g., "#DAA520"
  totalProductionDays: number;
  
  // New fields for Overall Production Progress
  totalScriptPages: number; // e.g., 110.5
  cumulativePagesShot: number; // e.g., 9.75
  totalScriptScenes: number; // e.g., 220
  cumulativeScenesShot: number; // e.g., 12
  
  // Current production day indicator (based on today's date vs schedule)
  currentDayNumber: number | null; // e.g., 5 means "we are on Day 5"
  
  days: TimingTowerDay[];
}

export interface ProductionAssumptionsMetric {
  name: string;
  original: number;
  current: number;
  variance: number;
}

// Option B: Multi-section budget tracking
export interface TotalBudgetSection {
  name: string;
  target: number;
  committed: number;
  variance: number;
  status: 'over' | 'under' | 'on_target';
}

export interface ProductionSpendingSection {
  name: string;
  budget: number;
  actuals: number;
  variance: number;
  daysCompleted: number;
  totalDays: number;
  costPerPage: number;
  status: 'tracking' | 'over' | 'under';
}

export interface PostProductionSection {
  name: string;
  budget: number;
  actuals: number;
  variance: number;
  status: 'reserved' | 'in_progress' | 'complete';
}

export interface ScheduleSection {
  name: string;
  original: number;
  current: number;
  variance: number;
  status: 'not_started' | 'on_track' | 'over' | 'complete';
}

export interface CreativeSection {
  name: string;
  originalPages: number;
  originalPagesFormatted: string;
  currentPages: number;
  currentPagesFormatted: string;
  pagesShot: number;
  pagesShotFormatted: string;
  originalScenes: number;
  currentScenes: number;
  scriptRevision: string;
  variance: number;
  status: 'baseline' | 'revised';
}

export interface ScriptRevisionBaseline {
  pages: number;
  pagesFormatted: string;
  scenes: number;
  schedule: number;
  costPerPage: number;
}

export interface ScriptRevisionCurrent {
  pages: number;
  pagesFormatted: string;
  scenes: number;
  omittedScenes: number;
  schedule: number;
  costPerPage: number;
}

export interface ScriptRevisionVariance {
  pages: number;
  scenes: number;
  omittedScenes: number;
  costPerPageDelta: number;
}

export interface ScriptRevisionSection {
  hasRevision: boolean;
  revisionColor: string;
  revisionDate: string | null;
  baseline: ScriptRevisionBaseline;
  current: ScriptRevisionCurrent;
  variance: ScriptRevisionVariance;
}

export interface ProductionAssumptionsData {
  productionTitle: string;
  baselineVersion: string;
  
  // Option B sections
  totalBudget?: TotalBudgetSection;
  productionSpending?: ProductionSpendingSection;
  postProduction?: PostProductionSection;
  schedule?: ScheduleSection;
  creative?: CreativeSection;
  
  // Script revision comparison data
  scriptRevision?: ScriptRevisionSection;
  
  // Legacy format for backward compatibility
  metrics: {
    schedule: ProductionAssumptionsMetric;
    budget: ProductionAssumptionsMetric;
    creative: ProductionAssumptionsMetric;
  };
}

// Document Tracking Types
export interface DocumentStatus {
  status: 'received' | 'pending' | 'due' | 'overdue' | 'missing' | 'unknown';
  receivedAt: string | null;
  hoursVariance: number | null;
}

export interface DayDocumentStatus {
  dayNumber: number;
  callSheet: DocumentStatus;
  dpr: DocumentStatus;
  hotCost: DocumentStatus;
}

export interface MissingDocument {
  documentType: string;
  dayNumber: number;
  status: string;
  hoursOverdue: number | null;
}

export interface DocumentTrackingSummary {
  totalExpected: number;
  totalReceived: number;
  completenessPercent: number;
  missingCallSheets: number;
  missingDprs: number;
  missingHotCosts: number;
  overdueCount: number;
}

export interface DocumentTrackingData {
  currentDay: number;
  summary: DocumentTrackingSummary;
  daysStatus: DayDocumentStatus[];
  missingDocuments: MissingDocument[];
}
